import React, { useState, useEffect } from "react";
import { useUserPermissions } from "../../../contexts/UserPermissionsContext";
import SidebarInfoText from "../../../offline/SidebarInfoText";
import "./Sidebar.css";
import logo from "images/logo.svg";
import pen from "images/pen.svg";
import { useNavigationGuard } from "../../../hooks/useNavigationGuard";
import { LuHouse, LuBook, LuSettings, LuMessageCircle, LuChevronLeft, LuChevronRight, LuX } from "react-icons/lu";
import UploadQueue from "../../common/UploadQueue/UploadQueue";

const Sidebar = ({ setSelectedPart, partName, forceCollapsed = false, onNavigate }) => {
  const { canUpload } = useUserPermissions();
  const customNavigate = useNavigationGuard();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [anlageVersion] = useState(() => Date.now());
  const [isAnlageZoomed, setIsAnlageZoomed] = useState(false);
  // anlage.png is per-tenant and frequently unset (404 for any tenant whose
  // facility image was never uploaded). Track load failure so we hide the whole
  // section instead of rendering a broken image + orphaned "Anlagenübersicht"
  // heading. (2026-06-09 fix.)
  const [anlageOk, setAnlageOk] = useState(true);

  useEffect(() => {
    if (!isAnlageZoomed) return;
    const onKey = (e) => { if (e.key === "Escape") setIsAnlageZoomed(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isAnlageZoomed]);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const effectiveCollapsed = isCollapsed || forceCollapsed;

  // Update CSS variable when sidebar collapses/expands
  useEffect(() => {
    const root = document.documentElement;
    if (effectiveCollapsed) {
      root.style.setProperty('--sidebar-width', '70px');
    } else {
      root.style.setProperty('--sidebar-width', '280px');
    }
  }, [effectiveCollapsed]);

  const handleLogoClick = () => {
    console.log(
      "Logo clicked, navigating to '/' and setting selected part to '1'"
    );
    onNavigate?.();
    customNavigate("/");
    setSelectedPart("1");
  };

  const handleSetupClick = () => {
    console.log("Setup clicked, navigating to '/setup'");
    onNavigate?.();
    customNavigate("/route/setup");
  };

  const handleDocumentsClick = () => {
    console.log("Documents clicked, navigating to '/documents'");
    onNavigate?.();
    customNavigate("/route/documents");
  };

  const handleAnlageClick = () => {
    console.log(
      "Anlage clicked, navigating to '/' and setting selected part to '1'"
    );
    onNavigate?.();
    customNavigate("/");
    setSelectedPart("1");
  };

  const handleFeedbackClick = () => {
    const feedback = window.prompt("Bitte geben Sie Ihr Feedback ein:");
    if (feedback) {
      // Checks if feedback is not null or empty
      console.log("Feedback received:", feedback);
      // Here you can add any functionality to send the feedback to a server or process it further
    } else {
      console.log("No feedback given.");
    }
  };

  const handleAnlageImageClick = () => {
    setIsAnlageZoomed(true);
  };

  return (
    <>
    <aside className={`sidebar ${effectiveCollapsed ? 'collapsed' : ''}`}>
      <button
        className="sidebar-toggle"
        onClick={toggleSidebar}
        aria-label={effectiveCollapsed ? "Sidebar erweitern" : "Sidebar minimieren"}
      >
        {effectiveCollapsed ? <LuChevronRight size={20} /> : <LuChevronLeft size={20} />}
      </button>
      <div className="sidebar-content">
        {/* Logo Section */}
        <div className="logo-section">
          <img
            className="sidebar-logo"
            src={effectiveCollapsed ? "/favicon.svg" : logo}
            alt="Logo"
            onClick={handleLogoClick}
          />
        </div>

        {/* System Overview - Moved to top. Hidden entirely if anlage.png is
            absent (404) so we never show a broken image / orphaned heading. */}
        {anlageOk && (
          <div className="anlage-section">
            <div className="nav-section-title">Anlagenübersicht</div>
            <img
              className="anlage"
              src={`/anlage.png?v=${anlageVersion}`}
              alt="Anlagenübersicht"
              title="Vergrößern"
              onClick={handleAnlageImageClick}
              onError={() => setAnlageOk(false)}
            />
          </div>
        )}
        
        {/* Primary Navigation */}
        <nav className="nav-primary">
          <button
            className={`nav-item ${window.location.pathname === '/' ? "active" : ""}`}
            onClick={handleAnlageClick}
            title={effectiveCollapsed ? "Arbeitsbereich" : ""}
          >
            <div className="nav-icon"><LuHouse size={20} /></div>
            <div className="nav-content">
              <div className="nav-label">Arbeitsbereich</div>
              <div className="nav-subtitle">{partName || "KI-Chat starten"}</div>
            </div>
          </button>

          <button
            className={`nav-item ${window.location.pathname === '/route/documents' ? "active" : ""}`}
            onClick={handleDocumentsClick}
            title={effectiveCollapsed ? "Dokumente" : ""}
          >
            <div className="nav-icon"><LuBook size={20} /></div>
            <div className="nav-content">
              <div className="nav-label">Dokumente</div>
              <div className="nav-subtitle">Alle Dokumente verwalten</div>
            </div>
          </button>

          {canUpload && <button
            className={`nav-item ${window.location.pathname === '/route/setup' ? "active" : ""}`}
            onClick={handleSetupClick}
            title={effectiveCollapsed ? "Einrichtung" : ""}
          >
            <div className="nav-icon"><LuSettings size={20} /></div>
            <div className="nav-content">
              <div className="nav-label">Einrichtung</div>
              <div className="nav-subtitle">Batch-Upload & Setup</div>
            </div>
          </button>}
        </nav>


        {/* Feedback */}
        <div className="feedback-section">
          <button
            className="nav-item feedback"
            onClick={handleFeedbackClick}
            title={effectiveCollapsed ? "Feedback senden" : ""}
          >
            <div className="nav-icon"><LuMessageCircle size={20} /></div>
            <div className="nav-content">
              <div className="nav-label">Feedback senden</div>
              <div className="nav-subtitle">Helfen Sie uns zu verbessern</div>
            </div>
          </button>
        </div>

        {/* Info */}
        <div className="sidebar-info">
          <SidebarInfoText />
        </div>

        {/* Disclaimer */}
        <div className="disclaimer">
          <p>
            Die Online-Doku gilt nur als Unterstützung und bringt keinerlei Verbindlichkeiten oder Haftungen mit sich. Die vorliegende Dokumentation in Papierform ist die Dokumentation gemäß Maschinenrichtlinie und hat stets Vorrang.
          </p>
        </div>
      </div>

      {/* Upload Queue — pinned to sidebar bottom, outside scrollable content */}
      <UploadQueue isCollapsed={effectiveCollapsed} />
    </aside>

    {isAnlageZoomed && anlageOk && (
      <div
        className="anlage-lightbox"
        onClick={() => setIsAnlageZoomed(false)}
        role="dialog"
        aria-label="Anlagenübersicht vergrößert"
      >
        <button
          className="anlage-lightbox-close"
          onClick={(e) => { e.stopPropagation(); setIsAnlageZoomed(false); }}
          aria-label="Schließen"
        >
          <LuX size={28} />
        </button>
        <img
          className="anlage-lightbox-img"
          src={`/anlage.png?v=${anlageVersion}`}
          alt="Anlagenübersicht"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}
    </>
  );
};

export default Sidebar;